import React, { useEffect, useState } from 'react';
import { Search, RefreshCw, Users, Building2 } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const LeaveBalances = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('ho');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchBalances = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/leave-balances');
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching leave balances:', error);
      toast.error('Failed to fetch leave balances');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances();
  }, []);

  const hoEmployees = employees.filter((employee) => !employee.isSSbranch);
  const branchEmployees = employees.filter((employee) => employee.isSSbranch);
  const activeList = tab === 'ho' ? hoEmployees : branchEmployees;

  const filteredList = activeList.filter((employee) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (employee.name || '').toLowerCase().includes(term)
      || (employee.employeeId || '').toLowerCase().includes(term);
  });

  const tabs = [
    { key: 'ho', label: 'HO Employees', icon: Building2, count: hoEmployees.length },
    { key: 'branch', label: 'Branch Employees', icon: Users, count: branchEmployees.length },
  ];

  const columns = tab === 'branch'
    ? ['S.No', 'Employee ID', 'Employee', 'Organization', 'SL', 'EL', 'CL', 'Comp Off']
    : ['S.No', 'Employee ID', 'Employee', 'Organization', 'SL', 'EL', 'Comp Off'];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">No Of Leaves</h1>
          <p className="text-gray-600">Current leave balance for every employee, by leave type</p>
        </div>
        <button onClick={fetchBalances} className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {tabs.map(({ key, label, icon: Icon, count }) => {
              const isActive = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    isActive
                      ? 'bg-primary-600 border-primary-600 text-white shadow-sm'
                      : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                  <span className={`ml-1 rounded-full px-1.5 text-xs ${isActive ? 'bg-white/20' : 'bg-gray-100'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">
            {tab === 'ho' ? 'HO Employees' : 'Branch Employees'}{' '}
            <span className="text-sm font-normal text-gray-400">({filteredList.length})</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <LoadingSpinner size="lg" className="py-12" />
          ) : filteredList.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No employees found</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/60">
                <tr>
                  {columns.map((heading) => (
                    <th key={heading} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredList.map((employee, index) => (
                  <tr key={employee.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">{index + 1}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-800">{employee.employeeId || 'N/A'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-primary-600">{(employee.name || '?')[0].toUpperCase()}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{employee.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{employee.organization || 'N/A'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">{employee.sick_leaves_left}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">{employee.earned_leaves_left}</td>
                    {tab === 'branch' && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">{employee.casual_leaves_left}</td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">{employee.comp_off_left}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveBalances;
